import type { UserInviteInput, UserUpdateInput } from '@bbl/validation';
import type { PortalUser, UserDetail } from '../components/system/types';

const API_ORIGIN =
  (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? 'https://api.babulilmonline.com';

/**
 * A link the API hands back ("/api/salaries/payments/…/payslip-file") as a
 * full address on the API's own site. Opened as-is, a path like that lands
 * on the portal's site, where it doesn't exist. Full addresses pass through.
 */
export const apiUrl = (pathOrUrl: string): string => new URL(pathOrUrl, API_ORIGIN).toString();

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    if (details !== undefined) this.details = details;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | undefined>;
}

const buildUrl = (path: string, query?: RequestOptions['query']): string => {
  const url = new URL(path, API_ORIGIN);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
};

/** The most rows a list endpoint sends in one page (`pageSize` above this is refused). */
export const MAX_PAGE_SIZE = 100;

let currentToken: string | null = null;
export const setApiToken = (token: string | null): void => {
  currentToken = token;
};

export const apiRequest = async <T>(path: string, opts: RequestOptions = {}): Promise<T> => {
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = opts.body
    ? { 'content-type': 'application/json', accept: 'application/json' }
    : { accept: 'application/json' };
  if (currentToken) {
    headers['authorization'] = `Bearer ${currentToken}`;
  }
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers,
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  if (opts.signal) init.signal = opts.signal;
  const response = await fetch(url, init);
  if (response.status === 204) {
    return undefined as T;
  }
  const json = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    const details =
      typeof json.details === 'object' && json.details !== null
        ? (json.details as Record<string, unknown>)
        : undefined;
    throw new ApiError(
      response.status,
      typeof json.code === 'string' ? json.code : 'unknown_error',
      typeof json.message === 'string' ? json.message : 'Request failed',
      details,
    );
  }
  return json as T;
};

/** Most rows `apiListAll` collects before giving up rather than showing part of a list. */
const LIST_ALL_MAX_PAGES = 50;

/**
 * Every row of a paged list, fetched 100 at a time. For screens that show a
 * whole list at once (a month's salaries, a picker). Fails, rather than
 * quietly showing part of it, past 5,000 rows.
 */
export const apiListAll = async <T>(
  path: string,
  opts: { signal?: AbortSignal; query?: RequestOptions['query'] } = {},
): Promise<T[]> => {
  const all: T[] = [];
  for (let page = 1; page <= LIST_ALL_MAX_PAGES; page += 1) {
    const res = await apiRequest<{ items?: T[]; total?: number }>(path, {
      ...(opts.signal ? { signal: opts.signal } : {}),
      query: { ...opts.query, page, pageSize: MAX_PAGE_SIZE },
    });
    const items = res.items ?? [];
    all.push(...items);
    if (items.length < MAX_PAGE_SIZE || (res.total !== undefined && all.length >= res.total)) return all;
  }
  throw new ApiError(413, 'list_too_long', 'This list is too long to show at once. Narrow it with a filter.');
};

/**
 * Fetch a file the API keeps behind sign-in (a payslip or receipt PDF).
 *
 * It sends the same sign-in as every other request. Opening the file's
 * address directly would rely on a sign-in cookie for the API's own site,
 * which Safari and iPhones don't keep for a site you aren't on — so the
 * file wouldn't open there.
 */
export const fetchApiFile = async (path: string, signal?: AbortSignal): Promise<{ blob: Blob; filename: string | null }> => {
  const headers: Record<string, string> = {};
  if (currentToken) headers['authorization'] = `Bearer ${currentToken}`;
  const response = await fetch(apiUrl(path), { credentials: 'include', headers, ...(signal ? { signal } : {}) });
  if (!response.ok) {
    const json = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    throw new ApiError(
      response.status,
      typeof json.code === 'string' ? json.code : 'file_failed',
      typeof json.message === 'string' ? json.message : 'Could not open the file.',
    );
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/u.exec(disposition)?.[1] ?? null;
  return { blob: await response.blob(), filename };
};

const showFile = (file: { blob: Blob; filename: string | null }, tab: Window | null, download: boolean): void => {
  const objectUrl = URL.createObjectURL(file.blob);
  if (download) {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = file.filename ?? 'document.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else if (tab) {
    tab.location.href = objectUrl;
  } else {
    window.open(objectUrl, '_blank', 'noopener');
  }
  // Long enough for the new tab to load it.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
};

/**
 * Open (or download) a document from an endpoint that hands out its link —
 * `…/payslip-url`, `…/receipt-url`. Right after a payment is marked paid the
 * PDF may still be being made; that says so instead of opening a broken page.
 */
export const openApiDocument = async (linkPath: string, opts: { download?: boolean } = {}): Promise<void> => {
  const tab = opts.download ? null : window.open('', '_blank');
  if (tab) tab.opener = null;
  try {
    const res = await apiRequest<{ url?: string; message?: string }>(linkPath);
    if (!res?.url) {
      throw new ApiError(202, 'document_pending', 'It’s still being prepared. Try again in a few seconds.');
    }
    showFile(await fetchApiFile(res.url), tab, !!opts.download);
  } catch (err) {
    tab?.close();
    throw err;
  }
};

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  createdAt: string;
  readAt: string | null;
  deepLink: string | null;
  kind?: string;
}

export interface NotificationPreferences {
  email: boolean;
  inApp: boolean;
  sms: boolean;
  digestFrequency: 'off' | 'daily' | 'weekly';
}

export interface ListResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export type ListParams = Record<string, string | number | boolean | undefined>;

const toQuery = (params?: ListParams): Record<string, string | number | undefined> | undefined => {
  if (!params) return undefined;
  const out: Record<string, string | number | undefined> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined) continue;
    out[k] = typeof v === 'boolean' ? String(v) : v;
  }
  return out;
};

export interface CrudResource<T, Create, Update> {
  list: (params?: ListParams, signal?: AbortSignal) => Promise<ListResponse<T>>;
  get: (id: string, signal?: AbortSignal) => Promise<T>;
  create: (body: Create) => Promise<T>;
  update: (id: string, body: Update) => Promise<T>;
  remove: (id: string) => Promise<void>;
}

export const crud = <T, Create, Update>(basePath: string): CrudResource<T, Create, Update> => {
  const root = basePath.replace(/\/$/, '');
  return {
    list: (params, signal) => {
      const opts: RequestOptions = {};
      const query = toQuery(params);
      if (query) opts.query = query;
      if (signal) opts.signal = signal;
      return apiRequest<ListResponse<T>>(root, opts);
    },
    get: (id, signal) => {
      const opts: RequestOptions = {};
      if (signal) opts.signal = signal;
      return apiRequest<T>(`${root}/${encodeURIComponent(id)}`, opts);
    },
    create: (body) => apiRequest<T>(root, { method: 'POST', body }),
    update: (id, body) =>
      apiRequest<T>(`${root}/${encodeURIComponent(id)}`, { method: 'PATCH', body }),
    remove: (id) =>
      apiRequest<void>(`${root}/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  };
};

export const listResource = <T>(
  path: string,
  params?: ListParams,
  signal?: AbortSignal,
): Promise<ListResponse<T>> => {
  const opts: RequestOptions = {};
  const query = toQuery(params);
  if (query) opts.query = query;
  if (signal) opts.signal = signal;
  return apiRequest<ListResponse<T>>(path, opts);
};

export const api = {
  me: () => apiRequest<MeResponse>('/api/me'),
  login: (body: { email: string; password: string; turnstileToken: string }) =>
    apiRequest<{ ok: true; sessionToken: string; user: { id: string; email: string; displayName: string; primaryRole: string; mustResetPassword: boolean } }>(
      '/api/auth/login',
      { method: 'POST', body },
    ),
  logout: () => apiRequest<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  requestReset: (body: { email: string; turnstileToken: string }) =>
    apiRequest<{ ok: true }>('/api/auth/request-reset', { method: 'POST', body }),
  resetPassword: (body: { token: string; newPassword: string; signInAfter?: boolean }) =>
    apiRequest<{
      ok: true;
      // Only present when `signInAfter: true` was passed and the
      // server accepted the reset.
      sessionToken?: string;
      user?: { id: string; email: string; displayName: string; primaryRole: string; mustResetPassword: boolean };
    }>('/api/auth/reset', { method: 'POST', body }),
  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    apiRequest<{ ok: true }>('/api/auth/change-password', { method: 'POST', body }),
  requestOtp: (body: { email: string; turnstileToken: string }) =>
    apiRequest<{ ok: true }>('/api/auth/otp/request', { method: 'POST', body }),
  verifyOtp: (body: { email: string; code: string }) =>
    apiRequest<{ ok: true; sessionToken: string; user: { id: string; email: string; primaryRole: string } }>(
      '/api/auth/otp/verify',
      { method: 'POST', body },
    ),
  dashboard: () =>
    apiRequest<{ role: string; kpis: Record<string, number | null | string> }>('/api/dashboard'),
  list: <T>(path: string, params?: ListParams, signal?: AbortSignal) =>
    listResource<T>(path, params, signal),
  crud,
  notifications: {
    list: (signal?: AbortSignal) => {
      const opts: RequestOptions = {};
      if (signal) opts.signal = signal;
      return apiRequest<ListResponse<NotificationItem>>('/api/notifications', opts);
    },
    markRead: (id: string) =>
      apiRequest<{ ok: true }>(
        `/api/notifications/${encodeURIComponent(id)}/read`,
        { method: 'POST' },
      ),
    markAllRead: () =>
      apiRequest<{ ok: true }>('/api/notifications/read-all', { method: 'POST' }),
    preferences: {
      get: () => apiRequest<NotificationPreferences>('/api/notifications/preferences'),
      patch: (body: Partial<NotificationPreferences>) =>
        apiRequest<NotificationPreferences>('/api/notifications/preferences', {
          method: 'PATCH',
          body,
        }),
    },
  },
  users: {
    list: (
      // The filter toolkit builds these params from `usersFilterConfig`
      // (q / status / role / lastLoginFrom / lastLoginTo / sort / page /
      // pageSize). Forward the whole record rather than allow-listing, so a new
      // facet param reaches the backend without touching this wrapper.
      params: Record<string, string | number | undefined> = {},
      signal?: AbortSignal,
    ) => {
      const query: Record<string, string | number | undefined> = {};
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) query[key] = value;
      }
      const opts: RequestOptions = { query };
      if (signal) opts.signal = signal;
      return apiRequest<ListResponse<PortalUser>>('/api/users', opts);
    },
    get: (id: string, signal?: AbortSignal) => {
      const opts: RequestOptions = {};
      if (signal) opts.signal = signal;
      return apiRequest<UserDetail>(`/api/users/${encodeURIComponent(id)}`, opts);
    },
    invite: (body: UserInviteInput) =>
      apiRequest<{
        id: string;
        email: string;
        primaryRole: Role;
        status: 'pending';
        // Returned so the admin can copy + share manually when email
        // delivery isn't configured. Same URL the user receives by email.
        activationUrl: string;
      }>('/api/users/invite', { method: 'POST', body }),
    reinvite: (id: string) =>
      apiRequest<{ ok: true; activationUrl: string }>(
        `/api/users/${encodeURIComponent(id)}/reinvite`,
        { method: 'POST' },
      ),
    /**
     * Link a user to an existing teacher / manager / student profile.
     * Used to fix up users that were created without a profile link at
     * invite time.
     */
    linkProfile: (
      id: string,
      body: { type: 'teacher' | 'manager' | 'student'; id: string },
    ) =>
      apiRequest<{ ok: true }>(
        `/api/users/${encodeURIComponent(id)}/link-profile`,
        { method: 'POST', body },
      ),
    /**
     * Super_admin sets a password on behalf of a user. Used when email
     * delivery isn't configured and the admin needs to give the user a
     * working password to sign in with. The server forces
     * must_reset_password=true so the user picks their own on first
     * login.
     */
    setPassword: (id: string, body: { newPassword: string }) =>
      apiRequest<{ ok: true }>(
        `/api/users/${encodeURIComponent(id)}/set-password`,
        { method: 'POST', body },
      ),
    update: (id: string, body: UserUpdateInput) =>
      apiRequest<{ ok: true; changed?: boolean }>(`/api/users/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body,
      }),
    deactivate: (id: string) =>
      apiRequest<{ ok: true; alreadyInactive?: boolean }>(
        `/api/users/${encodeURIComponent(id)}/deactivate`,
        { method: 'POST' },
      ),
    reactivate: (id: string) =>
      apiRequest<{ ok: true; alreadyActive?: boolean }>(
        `/api/users/${encodeURIComponent(id)}/reactivate`,
        { method: 'POST' },
      ),
    forceReset: (id: string) =>
      apiRequest<{ ok: true }>(`/api/users/${encodeURIComponent(id)}/force-reset`, {
        method: 'POST',
      }),
  },
};

/** Account language. Kept in sync with `users.locale` (migration 0028). */
export type Locale = 'en' | 'ml';

export interface MeResponse {
  id: string;
  email: string;
  displayName: string;
  primaryRole: Role;
  /** UI + notification language for this account. Parents may pick 'ml'. */
  locale: Locale;
  roles: Role[];
  mfaEnrolled: boolean;
  passkeyEnrolled: boolean;
  mustResetPassword: boolean;
  householdId: string | null;
  teacherId: string | null;
  studentId: string | null;
  createdAt: string;
  /**
   * When this person first opened each menu link after it became new
   * (link path → India day). Drives the NEW badge; `null` or missing when it
   * couldn't load, and the badges then fall back to the 15-day rule alone.
   */
  linkOpens?: Record<string, string> | null;
}

export type Role =
  | 'super_admin'
  | 'academic_admin'
  | 'finance_admin'
  | 'nalim'
  | 'teacher'
  | 'parent'
  | 'student'
  | 'support_viewer'
  // Added in Phase 4 prep for Hiring (Module 1). Mirrors the validation
  // package's roleSchema; required for the future Hiring portal screens.
  | 'candidate'
  | 'hr';
